import {
  Divider,
  Select,
  Table,
  Spin,
  Button,
  message,
  Modal,
  Switch,
  Tooltip,
  Image,
} from 'antd'
import { useEffect, useState } from 'react'
import {
  getPost,
  getUserPosts,
  deleteContentById,
  updateAdminState,
} from 'src/api/social'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { fetchCountryProvider } from 'src/api/authen'
import { getUsers } from 'src/api/user'
import './Userpost.css'
import { extractTextContent } from 'src/utils/stringUtil'
import {
  DeleteOutlined,
  CommentOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { longToTimestamp } from 'src/utils/timestamp'
import { downloadImageById } from 'src/api/image'
import { ADMINCHECKSTATE } from 'src/constants'

const { confirm } = Modal

const UserPosts = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [providerCountry, setProviderCountry] = useState('TN')
  const [userData, setUserData] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [userId, setUserId] = useState(undefined)
  const [countries, setCountries] = useState([])
  const [badContent, setBadContent] = useState(false)
  const [loading, setLoading] = useState(true)
  const [adminCheckState, setAdminCheckState] = useState('')
  const [transformedData, setTransformedData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [rowLoading, setRowLoading] = useState<{ [postId: string]: boolean }>(
    {}
  )
  const [sortField, setSortField] = useState('createdOn')
  const [sortOrder, setSortOrder] = useState('DESC')
  const [imageLoadErrors, setImageLoadErrors] = useState({})
  const params = new URLSearchParams()

  const handleClickOpen = (record) => {
    params.append('botUserId', userId)
    params.append('text', record.text)
    navigate(`/social/user-posts/comments/${record.id}?${params.toString()}`)
  }

  const handleDelete = async (record) => {
    try {
      setLoading(true)
      await deleteContentById(record, 'SOCIAL-POST')
      message.success('Content deleted successfully!')
      if (id) {
        setTransformedData([])
      } else {
        fetchUserPostData(
          currentPage,
          pageSize,
          userId,
          providerCountry,
          sortField,
          sortOrder
        )
      }
    } catch (error) {
      message.error('Failed to delete content.')
    }
  }

  const transformData = async (data) => {
    if (!data) return []

    if (Array.isArray(data)) {
      return data.map((item) => ({
        ...item,
        key: item.id,
        username: item.user.username,
        createdOn: longToTimestamp(item.created_on),
        modifiedOn: longToTimestamp(item.modified_on),
        resourceContents: null,
      }))
    } else {
      return [
        {
          ...data,
          key: data.id,
          username: data.user?.username || 'Unknown',
          createdOn: longToTimestamp(data.created_on),
          modifiedOn: longToTimestamp(data.modified_on),
          resourceContents: null,
        },
      ]
    }
  }

  const getImages = async (attachments) => {
    if (!attachments || attachments.length === 0) return []
    return await Promise.all(
      attachments.map((attachment) =>
        downloadImageById(attachment.attachmentId)
      )
    )
  }

  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize } = pagination
    setCurrentPage(current)
    setPageSize(pageSize)

    let sortField = 'createdOn'
    let sortOrder = 'DESC'

    if (sorter?.field === 'createdOn' || sorter?.field === 'modifiedOn') {
      sortField = sorter.field
      sortOrder = sorter.order === 'ascend' ? 'ASC' : 'DESC'
    }

    setSortField(sortField)
    setSortOrder(sortOrder)

    if (!id) {
      fetchUserPostData(
        current,
        pageSize,
        userId,
        providerCountry,
        sortField,
        sortOrder
      )
    }
  }

  const fetchPostData = async () => {
    setLoading(true)
    try {
      const response = await getPost(id)
      const transformedData = await transformData(response.data)
      setTransformedData(transformedData)
      setTotalRecords(1)
    } catch (error) {
      message.error('Failed to load post data.')
      setTransformedData([])
    } finally {
      setLoading(false)
    }
  }

  const fetchUserPostData = async (
    page,
    size,
    userId,
    providerCountry,
    sortField = 'createdOn',
    sortOrder = 'DESC'
  ) => {
    try {
      setLoading(true)
      const response = await getUserPosts(
        page - 1,
        size,
        providerCountry,
        userId,
        badContent,
        adminCheckState,
        sortField,
        sortOrder
      )
      const transformedData = await transformData(response.data)
      setTransformedData(transformedData)
      setTotalRecords(response.total_items)
    } catch (error) {
      message.error('Failed to load post data.')
      setTransformedData([])
    } finally {
      setLoading(false)
    }
  }

  const fetchImagesForRows = async (isCancelled) => {
    const updatedData = await Promise.all(
      transformedData.map(async (item) => {
        if (!item.resourceContents && item.attachments?.length) {
          setRowLoading((prev) => ({ ...prev, [item.id]: true }))
          try {
            const resourceContents = await getImages(item.attachments)
            if (!isCancelled) {
              return { ...item, resourceContents }
            }
          } catch (e) {
            console.error('Image loading failed', e)
          } finally {
            setRowLoading((prev) => ({ ...prev, [item.id]: false }))
          }
        }
        return item
      })
    )

    if (!isCancelled) {
      setTransformedData(updatedData)
    }
  }

  const handleProviderCountryChange = (value) => {
    setProviderCountry(value)
    if (!id) {
      fetchUserPostData(
        currentPage,
        pageSize,
        userId,
        value,
        sortField,
        sortOrder
      )
    }
  }

  const handleToggleBadContent = (checked) => {
    setBadContent(checked)
    setCurrentPage(1)
    if (!id) {
      fetchUserPostData(
        1,
        pageSize,
        userId,
        providerCountry,
        sortField,
        sortOrder
      )
    }
  }

  const seachByAdminState = (value) => {
    setAdminCheckState(value)
    if (!id) {
      fetchUserPostData(
        currentPage,
        pageSize,
        userId,
        providerCountry,
        sortField,
        sortOrder
      )
    }
  }

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = data.data.map((item) => ({
        label: item.countryName,
        value: item.countryCode,
      }))
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const fetchUsers = async (searchValue = '', reset = false) => {
    if (loadingUsers || (!reset && !hasMore)) return

    setLoadingUsers(true)

    try {
      const currentPage = reset ? 0 : page
      const response = await getUsers(
        currentPage,
        10,
        searchValue,
        providerCountry
      )

      if (response.data.length === 0) {
        setHasMore(false)
      }

      const usersList = response.data.map((element) => ({
        label: element.userName,
        value: element.userId,
      }))

      setUserData((prevUsers) => {
        const mergedUsers = reset ? usersList : [...prevUsers, ...usersList]
        return Array.from(
          new Map(mergedUsers.map((user) => [user.value, user])).values()
        )
      })

      setPage(reset ? 1 : currentPage + 1)
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  const showDeleteConfirm = (record) => {
    confirm({
      title: 'Are you sure you want to delete this content?',
      content: 'Once deleted, this action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: () => handleDelete(record),
      onCancel: () => {
        message.info('Deletion cancelled.')
      },
    })
  }

  const handleStateChange = async (value, record) => {
    setLoading(true)
    try {
      await updateAdminState('POST', record, value)
      record.admin_check_state = value
      setTransformedData([...transformedData])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'username',
      key: 'username',
      sorter: false,
    },
    {
      title: 'Text',
      dataIndex: 'text',
      key: 'text',
      width: 200,
      sorter: false,
      render: (text) => (
        <Tooltip title={extractTextContent(text)}>
          {text?.length > 30
            ? extractTextContent(text)?.substring(0, 30) + '...'
            : extractTextContent(text)}
        </Tooltip>
      ),
    },
    {
      title: 'Attachments',
      dataIndex: 'resourceName',
      key: 'resourceName',
      width: 500,
      sorter: false,
      render: (_, record) => {
        const isLoadingRow = rowLoading[record.id]
        if (isLoadingRow) {
          return (
            <div style={{ textAlign: 'center' }}>
              <Spin size="small" />
            </div>
          )
        }
        if (record.resourceContents && record.resourceContents.length > 0) {
          return (
            <div className="attachments-scroll">
              <Image.PreviewGroup
                preview={{
                  onVisibleChange: () => {
                    console.log('change')
                  },
                }}
              >
                <div
                  className="attachments-gallery"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    padding: '8px',
                    background: '#f9f9f9',
                    borderRadius: '8px',
                  }}
                >
                  {record.resourceContents.map((content, index) => {
                    const imageFailed = imageLoadErrors[content]
                    return (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          width: '80px',
                          height: '80px',
                          flexShrink: 0,
                        }}
                      >
                        {!imageFailed ? (
                          <div
                            style={{
                              width: '80px',
                              height: '80px',
                              flexShrink: 0,
                              overflow: 'hidden',
                              borderRadius: '6px',
                              border: '1px solid #141313',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              backgroundColor: '#141313',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <Image
                              src={content}
                              alt={`Attachment ${index + 1}`}
                              preview
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={() =>
                                setImageLoadErrors((prev) => ({
                                  ...prev,
                                  [content]: true,
                                }))
                              }
                            />
                          </div>
                        ) : (
                          <a
                            href={content}
                            download
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              background: '#f0f0f0',
                              textAlign: 'center',
                              fontSize: '12px',
                              color: '#1890ff',
                              textDecoration: 'none',
                            }}
                            aria-label={`Download attachment ${index + 1}`}
                          >
                            <DownloadOutlined style={{ marginRight: '4px' }} />
                            Download
                          </a>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Image.PreviewGroup>
            </div>
          )
        }
        return null
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 250,
      sorter: true,
    },
    {
      title: 'Modified On',
      dataIndex: 'modifiedOn',
      key: 'modifiedOn',
      width: 250,
      sorter: true,
    },
    {
      title: 'Admin State',
      dataIndex: 'admin_check_state',
      key: 'admin_check_state',
      sorter: false,
      render: (_, record) => (
        <Select
          value={record.admin_check_state}
          onChange={(value) => handleStateChange(value, record)}
          options={ADMINCHECKSTATE}
        />
      ),
      width: 250,
    },
    {
      title: 'Comments',
      key: 'View Comment',
      sorter: false,
      render: (_, record) => (
        <Button type="primary" onClick={() => handleClickOpen(record)}>
          <CommentOutlined />
        </Button>
      ),
    },
    {
      title: '',
      key: 'delete',
      sorter: false,
      render: (_, record) => (
        <Button type="primary" danger onClick={() => showDeleteConfirm(record)}>
          <DeleteOutlined />
        </Button>
      ),
    },
  ]

  useEffect(() => {
    fetchCountry()
    if (id) {
      fetchPostData()
    } else {
      fetchUserPostData(
        currentPage,
        pageSize,
        userId,
        providerCountry,
        sortField,
        sortOrder
      )
    }
  }, [
    id,
    currentPage,
    pageSize,
    userId,
    providerCountry,
    badContent,
    adminCheckState,
    sortField,
    sortOrder,
  ])

  useEffect(() => {
    let isCancelled = false
    fetchImagesForRows(isCancelled)
    return () => {
      isCancelled = true
    }
  }, [JSON.stringify(transformedData.map((d) => d.id + d.attachments?.length))])

  return (
    <>
      <Spin spinning={loading}>
        {!id && (
          <div>
            <Select
              placeholder="Select Country"
              value={providerCountry}
              onChange={handleProviderCountryChange}
              options={countries}
              style={{ marginLeft: '15px', width: '15%' }}
            />
            <Select
              showSearch
              allowClear
              placeholder="Search Username"
              value={searchTerm || undefined}
              onSearch={(value) => {
                setSearchTerm(value)
                setPage(0)
                setHasMore(true)
                fetchUsers(value, true)
              }}
              onClear={() => {
                setUserId(undefined)
                setSearchTerm('')
                setCurrentPage(1)
                setPageSize(10)
                if (!id) {
                  fetchUserPostData(1, 10, '', providerCountry)
                }
              }}
              onBlur={() => {
                if (!userId) {
                  setSearchTerm('')
                }
              }}
              onPopupScroll={(e) => {
                const bottom =
                  e.currentTarget.scrollHeight ===
                  e.currentTarget.scrollTop + e.currentTarget.clientHeight
                if (bottom && hasMore && !loadingUsers) {
                  fetchUsers(searchTerm, false)
                }
              }}
              onChange={(value) => {
                setUserId(value)
                setSearchTerm(
                  userData.find((user) => user.value === value)?.label || ''
                )
                setCurrentPage(1)
                setPageSize(10)
                if (!id) {
                  fetchUserPostData(1, 10, value, providerCountry)
                }
              }}
              loading={loadingUsers}
              filterOption={false}
              notFoundContent={
                loadingUsers ? <Spin size="small" /> : 'No users found'
              }
              style={{ marginLeft: 10, width: '200px' }}
            >
              {userData.map((user) => (
                <Select.Option key={user.value} value={user.value}>
                  {user.label}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Select State"
              onChange={seachByAdminState}
              value={adminCheckState || undefined}
              options={ADMINCHECKSTATE}
              allowClear
              style={{ marginLeft: '15px', width: '15%' }}
            />
            <Switch
              checked={badContent}
              onChange={handleToggleBadContent}
              style={{ marginLeft: '15px' }}
              checkedChildren="Bad Content"
              unCheckedChildren="All Posts"
            />
          </div>
        )}
        <Divider />
        <DndProvider backend={HTML5Backend}>
          <Table
            dataSource={transformedData}
            columns={columns}
            rowClassName={(record) =>
              record.text_bad_content || record.image_bad_content
                ? 'bad-content-row'
                : ''
            }
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: totalRecords,
            }}
            onChange={handleTableChange}
          />
        </DndProvider>
      </Spin>
    </>
  )
}

export default UserPosts
